import VConsole from 'vconsole';
import { ENABLE_VCONSOLE } from '@/config/env';

// vconsole 实例
let vConsole: VConsole | null = null;

/**
 * 初始化vConsole按钮的拖拽功能
 */
const initDraggable = () => {
  // 等待vConsole按钮渲染完成
  const interval = setInterval(() => {
    const vConsoleBtn = document.querySelector('.vc-switch');

    if (vConsoleBtn) {
      clearInterval(interval);

      // 设置按钮样式使其可定位
      Object.assign((vConsoleBtn as HTMLElement).style, {
        position: 'fixed',
        zIndex: '99999',
        touchAction: 'none',
        cursor: 'move',
        transition: 'none', // 禁用过渡动画，避免拖动时卡顿
        bottom: '40%',
        height: '32px',
        width: '94px'
      });

      let isDragging = false;
      let offsetX = 0;
      let offsetY = 0;

      // 开始拖动
      const startDrag = (e: MouseEvent | TouchEvent) => {
        isDragging = true;
        const btnRect = vConsoleBtn.getBoundingClientRect();

        if (e instanceof MouseEvent) {
          offsetX = e.clientX - btnRect.left;
          offsetY = e.clientY - btnRect.top;
        } else if (e.touches && e.touches.length > 0) {
          offsetX = e.touches[0].clientX - btnRect.left;
          offsetY = e.touches[0].clientY - btnRect.top;
        }

        // 添加事件监听
        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', stopDrag);
        document.addEventListener('touchmove', handleDrag);
        document.addEventListener('touchend', stopDrag);
      };

      // 处理拖动
      const handleDrag = (e: MouseEvent | TouchEvent) => {
        if (!isDragging) return;
        e.preventDefault();

        let clientX = 0;
        let clientY = 0;

        if (e instanceof MouseEvent) {
          clientX = e.clientX;
          clientY = e.clientY;
        } else if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
          clientY = e.touches[0].clientY;
        }

        // 计算新位置
        let newLeft = clientX - offsetX;
        let newTop = clientY - offsetY;

        // 限制在可视区域内
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const btnWidth = (vConsoleBtn as HTMLElement).offsetWidth;
        const btnHeight = (vConsoleBtn as HTMLElement).offsetHeight;

        newLeft = Math.max(0, Math.min(newLeft, windowWidth - btnWidth));
        newTop = Math.max(0, Math.min(newTop, windowHeight - btnHeight));

        // 设置新位置，覆盖默认的right和bottom
        Object.assign((vConsoleBtn as HTMLElement).style, {
          left: `${newLeft}px`,
          top: `${newTop}px`,
          right: 'auto',
          bottom: 'auto'
        });
      };

      // 停止拖动
      const stopDrag = () => {
        isDragging = false;
        // 移除事件监听
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', stopDrag);
        document.removeEventListener('touchmove', handleDrag);
        document.removeEventListener('touchend', stopDrag);
      };

      // 添加拖拽事件监听
      vConsoleBtn.addEventListener('mousedown', startDrag);
      vConsoleBtn.addEventListener('touchstart', startDrag);

      console.log('🔄 vconsole 按钮已支持拖拽');
    }
  }, 100); // 轮询检查按钮是否存在
};

/**
 * 初始化 vconsole
 * 只在开发环境下启用
 */
export const initVConsole = (): void => {
  // 只在配置启用时启用
  // if (!ENABLE_VCONSOLE) {
  //   console.log('vconsole 已禁用');
  //   return;
  // }

  // 避免重复初始化
  if (vConsole) {
    console.log('vconsole 已经初始化');
    return;
  }

  try {
    vConsole = new VConsole({
      theme: 'light', // 主题：light 或 dark
      defaultPlugins: ['system', 'network', 'element', 'storage'], // 默认插件
      maxLogNumber: 1000, // 最大日志数量
      disableLogScrolling: false, // 是否禁用日志滚动
      onReady: () => {
        console.log('🎉 vconsole 初始化成功');
        const __vconsole = document.getElementById('__vconsole');
        console.log(__vconsole, '__vconsole');
        if (__vconsole && !ENABLE_VCONSOLE) {
          __vconsole.style.display = 'none';
        }

        // 初始化拖拽功能
        initDraggable();
      },
      onClearLog: () => {
        console.log('🧹 vconsole 日志已清除');
      }
    });

    // 添加一些调试信息
    console.log('🚀 开发环境调试工具已启用');
    // console.log('📊 当前环境:', import.meta.env.MODE);
    // console.log('🌐 当前域名:', window.location.host);
    // console.log('📱 用户代理:', navigator.userAgent);

    // 添加自定义调试命令
    (window as any).debug = {
      vconsole: vConsole,
      clearLogs: () => {
        if (vConsole) {
          vConsole.destroy();
          initVConsole();
        }
      },
      show: () => vConsole?.show(),
      hide: () => vConsole?.hide(),
      version: '1.0.0'
    };

    console.log('💡 使用 window.debug 可以控制 vconsole');
  } catch (error) {
    console.error('❌ vconsole 初始化失败:', error);
  }
};

/**
 * 销毁 vconsole
 */
export const destroyVConsole = (): void => {
  if (vConsole) {
    try {
      vConsole.destroy();
      vConsole = null;
      console.log('vconsole 已销毁');
    } catch (error) {
      console.error('vconsole 销毁失败:', error);
    }
  }
};

/**
 * 切换 vconsole 显示状态
 */
export const toggleVConsole = (): void => {
  if (vConsole) {
    try {
      // 切换显示/隐藏状态
      if ((vConsole as any).dom.display === 'none') {
        vConsole.show();
      } else {
        vConsole.hide();
      }
    } catch (error) {
      console.error('vconsole 切换失败:', error);
    }
  }
};

/**
 * 检查 vconsole 是否已初始化
 */
export const isVConsoleReady = (): boolean => {
  return vConsole !== null;
};

/**
 * 获取 vconsole 实例
 */
export const getVConsole = (): VConsole | null => {
  return vConsole;
};

// 自动初始化（可选）
if (ENABLE_VCONSOLE) {
  // 延迟初始化，确保页面加载完成
  setTimeout(() => {
    initVConsole();
  }, 1000);
}
